import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, FileText, PenLine } from "lucide-react";
import { SignDocument } from "@/components/SignDocument";
import { ReviewQueue } from "@/components/ReviewQueue";
import { fmtDate } from "@/lib/dates";

export default function Paperwork() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [signing, setSigning] = useState(null);
  const fileRefs = useRef({});
  const isStudent = user.role === "Student";
  const load = () => { if (isStudent) api.get("/paperwork-assignments").then(({ data }) => setRows(data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const pickFile = (id) => fileRefs.current[id]?.click();

  const onFile = async (a, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(a.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("notes", "");
      await api.post(`/paperwork-assignments/${a.id}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${a.name} uploaded & submitted`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); e.target.value = ""; }
  };

  const review = async (a, decision) => {
    await api.post(`/paperwork-assignments/${a.id}/review`, { decision, rejection_reason: decision === "Rejected" ? "Needs resubmission" : "" });
    toast.success(decision); load();
  };

  const done = rows.filter((r) => r.status === "Approved").length;
  const fileHref = (loc) => (loc?.startsWith("http") ? loc : `${process.env.REACT_APP_BACKEND_URL}${loc}`);

  return (
    <div>
      <PageHeader title={isStudent ? "My Paperwork" : "Paperwork Review Queue"} subtitle={isStudent ? `${done}/${rows.length} approved — download each blank form, complete it, then upload it to be cleared` : "Review what students have submitted, then clear them to start — grouped by student so nothing disappears mid-review"} />
      {!isStudent ? <ReviewQueue /> : (
      <Card className="overflow-hidden">
        {rows.length === 0 ? (
          isStudent ? (
            <div className="text-center py-10 px-6">
              <p className="text-sm text-zinc-600 font-medium">No documents have been assigned to you yet.</p>
              <p className="text-xs text-zinc-500 mt-2 max-w-md mx-auto">
                Your document packet appears here as soon as your placement is confirmed by the facility and your
                Campus Coordinator assigns it. If your rotation has already been confirmed, message your Campus
                Coordinator and ask them to assign your document packet.
              </p>
            </div>
          ) : <EmptyState text="Nothing pending review." />
        ) : (
          <div className="space-y-2">
            {rows.map((a) => (
              <div key={a.id} className="flex items-center justify-between border rounded-md p-3" data-testid={`pw-item-${a.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-zinc-800 text-sm">{a.name}</p>
                  <p className="text-xs text-zinc-400">
                    {a.doc_type === "upload" ? "Upload your document" : (a.has_template ? "Download, complete & upload" : "Complete & upload")}
                    {" · "}Due {fmtDate(a.due_date)}
                    {a.file_name ? ` · 📎 ${a.file_name}` : ""}
                    {a.rejection_reason ? ` · ${a.rejection_reason}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.has_template && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/paperwork-assignments/${a.id}/template`} target="_blank" rel="noreferrer" className="text-cc-blue text-xs flex items-center gap-1" data-testid={`download-blank-${a.id}`}><FileText className="w-3.5 h-3.5" />Blank form</a>}
                  {a.file_location && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/paperwork-assignments/${a.id}/file`} target="_blank" rel="noreferrer" className="text-cc-blue text-xs flex items-center gap-1" data-testid={`view-file-${a.id}`}><FileText className="w-3.5 h-3.5" />View</a>}
                  <StatusBadge status={a.status} />
                  {isStudent && ["Assigned", "Rejected", "In Progress", "Not Started"].includes(a.status) && a.sign_in_app && (
                    <Button size="sm" className="bg-cc-blue text-white" onClick={() => setSigning(a)} data-testid={`sign-${a.id}`}>
                      <PenLine className="w-4 h-4 mr-1" />Read &amp; sign
                    </Button>
                  )}
                  {isStudent && ["Assigned", "Rejected", "In Progress", "Not Started"].includes(a.status) && !a.sign_in_app && (
                    <>
                      <input type="file" className="hidden" ref={(el) => (fileRefs.current[a.id] = el)} onChange={(e) => onFile(a, e)} data-testid={`file-input-${a.id}`} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                      <Button size="sm" variant="outline" disabled={busy === a.id} onClick={() => pickFile(a.id)} data-testid={`submit-${a.id}`}>
                        {busy === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" />Upload &amp; Submit</>}
                      </Button>
                    </>
                  )}
                  {!isStudent && ["Submitted", "Pending Review"].includes(a.status) && (
                    <>
                      <Button size="sm" className="bg-cc-green text-white" onClick={() => review(a, "Approved")} data-testid={`approve-${a.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => review(a, "Rejected")} data-testid={`reject-${a.id}`}>Reject</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      )}
      <SignDocument assignment={signing} onClose={() => setSigning(null)} onSigned={load} />
    </div>
  );
}
